/**
 * Hook for transforming card data into display-ready format.
 * Handles canonicalInfo integration and fallback logic.
 */

import { useMemo } from 'react';
import { CardSchema } from '../../../constants/graphqlTypes';
import { CardDisplayData, CardSource } from '../types';
import { getMediaType } from './useMediaType';

/**
 * Extract sources from canonical info.
 */
function extractSources(card: CardSchema): CardSource[] {
  const canonicalSources = card.canonicalInfo?.allSources;
  if (!canonicalSources || canonicalSources.length === 0) {
    return [];
  }

  return canonicalSources
    .filter((source): source is NonNullable<typeof source> => source !== null)
    .map((source) => ({
      sourceName: source.sourceName ?? '',
      externalLink: source.externalLink ?? '',
      isPrimary: source.isPrimary ?? false,
    }));
}

/**
 * Extract media tag names from card data.
 * Supports both the convenience field (mediaTagNames) and nested structure (mediaTags.items).
 */
function extractMediaTagNames(card: CardSchema): string[] {
  // First try the convenience field
  if (card.mediaTagNames && card.mediaTagNames.length > 0) {
    return card.mediaTagNames;
  }

  // Fall back to nested mediaTags structure
  if (card.mediaTags?.items && card.mediaTags.items.length > 0) {
    return card.mediaTags.items
      .filter((tag): tag is NonNullable<typeof tag> => tag !== null && tag.name !== null)
      .map((tag) => tag.name as string);
  }

  return [];
}

/**
 * Extract image URL from card data with multiple fallbacks.
 * Matches the old Card component's priority: displayThumbnailUrl first,
 * then cardStyle thumbnailImageSet, then canonicalInfo as last resort.
 */
function extractImageUrl(card: CardSchema): string | null {
  // 1. Try convenience field (matches old Card behavior)
  if (card.displayThumbnailUrl) {
    return card.displayThumbnailUrl;
  }

  // 2. Fall back to cardStyle.thumbnailImageSet
  const thumbnailImages = card.cardStyle?.thumbnailImageSet?.images;
  if (thumbnailImages && thumbnailImages.length > 0) {
    const firstImage = thumbnailImages[0];
    if (firstImage?.url) {
      return firstImage.url;
    }
  }

  // 3. Try canonical info display image as last resort
  if (card.canonicalInfo?.displayImageUrl) {
    return card.canonicalInfo.displayImageUrl;
  }

  return null;
}

/**
 * Normalize a color value that may be a plain hex string or a JSON gradient
 * string (e.g. '{"start":"#38322f","end":"#231f1d"}').
 * Returns a single hex color (preferring the "start" key for gradients) or null.
 */
function normalizeColor(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed);
      return parsed.start ?? parsed.end ?? null;
    } catch {
      return null;
    }
  }
  // Plain hex color
  return trimmed;
}

/**
 * Extract background color from card data with fallbacks.
 */
function extractBackgroundColor(card: CardSchema): string | null {
  // Try convenience field first
  if (card.displayBackgroundColor) {
    return normalizeColor(card.displayBackgroundColor);
  }

  // Fall back to cardStyle.bgColor
  if (card.cardStyle?.bgColor?.colorValue) {
    return normalizeColor(card.cardStyle.bgColor.colorValue);
  }

  return null;
}

/**
 * Extract external link URL from card data.
 */
function extractExternalLink(card: CardSchema): string | null {
  // Try externalSources first (used by existing cards)
  const externalSource = card.externalSources?.items?.[0];
  if (externalSource?.sourceUrl) {
    return externalSource.sourceUrl;
  }

  // Try canonical info sources
  const canonicalSources = card.canonicalInfo?.allSources;
  if (canonicalSources && canonicalSources.length > 0) {
    const primarySource = canonicalSources.find((s) => s?.isPrimary);
    if (primarySource?.externalLink) {
      return primarySource.externalLink;
    }
    // Fall back to first source
    if (canonicalSources[0]?.externalLink) {
      return canonicalSources[0].externalLink;
    }
  }

  return null;
}

/**
 * Hook to transform CardSchema into display-ready data.
 * Uses canonicalInfo when available with fallback to card data.
 *
 * @param card - The card data from GraphQL
 * @returns Processed display data
 */
export function useCardData(card: CardSchema): CardDisplayData {
  return useMemo(() => {
    // Use canonical data if available, fallback to card data.
    // Treat "Untitled" as missing so we can fall back to card.title.
    const canonicalTitle = card.canonicalInfo?.displayTitle;
    const title = (canonicalTitle && canonicalTitle !== 'Untitled' ? canonicalTitle : null) ?? card.title ?? '';
    const imageUrl = extractImageUrl(card);
    const backgroundColor = extractBackgroundColor(card);
    const sources = extractSources(card);
    const mediaTagNames = extractMediaTagNames(card);
    const mediaType = getMediaType(mediaTagNames);
    const sourceDomain = card.primarySourceDomain ?? card.sourceName ?? null;
    const picksCount = card.picksCount ?? 0;
    const commentsCount = card.commentsCount ?? 0;
    const externalLink = extractExternalLink(card);

    return {
      title,
      imageUrl,
      backgroundColor,
      sources,
      mediaType,
      sourceDomain,
      picksCount,
      commentsCount,
      externalLink,
    };
  }, [card]);
}

export default useCardData;
